import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";

import {
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { type SavedSearchOperator } from "@north-beam/nb-client-side";

import {
  SavedSearchOperatorLabels,
  savedSearchOperatorOptions,
} from "../utils";

interface Props {
  isOpen: boolean;
  value: SavedSearchOperator;
  showCampaignLabel: boolean;
  onChange(value: SavedSearchOperator): void;
  onOpenChange(value: boolean): void;
}

export const SearchOperatorSelect = ({
  isOpen,
  value,
  showCampaignLabel,
  onChange,
  onOpenChange,
}: Props) => {
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange,
    transform: false,
    placement: "bottom-start",
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  return (
    <div>
      <button
        className={clsx(
          "flex justify-between items-center text-sm font-light cursor-pointer",
          "rounded pl-1 pr-1.5 max-w-[100px] h-5 mx-2",
          {
            "text-cosmic+1 hover:bg-cosmic-5": !isOpen,
            "text-white bg-primary": isOpen,
          },
        )}
        ref={refs.setReference}
        {...getReferenceProps()}
        onMouseDown={(e) => e.preventDefault()}
      >
        <span className="truncate">{SavedSearchOperatorLabels[value]}</span>
        <span className="pl-1.5 text-xs">
          <motion.i
            initial={false}
            animate={{ rotate: isOpen ? 0 : 180 }}
            transition={{ duration: 0.2 }}
            className="fa-regular fa-sharp fa-chevron-up"
          />
        </span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            transition={{ ease: "easeOut", duration: 0.2 }}
            initial={{ opacity: 0, translateY: "0.25rem" }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: "0.25rem" }}
            ref={refs.setFloating}
            style={floatingStyles}
            className={clsx(
              "absolute overflow-y-auto z-10 mt-1.5 px-0 py-1 bg-white rounded-lg elevation-1",
              "text-sm max-h-[20rem] w-44",
            )}
            {...getFloatingProps()}
          >
            <div className="text-xs font-light text-gray+1 px-4 py-2">
              Search campaign, adset, and ad names.
              <br />
              {showCampaignLabel && "And campaign source and medium."}
            </div>
            {savedSearchOperatorOptions().map((e) => (
              <div
                key={e.value}
                className={clsx(
                  "px-4 py-2 cursor-pointer text-interstellar font-normal",
                  e.value === value
                    ? "bg-cosmic-3 font-semibold"
                    : "hover:bg-cosmic-4",
                )}
                onClick={() => {
                  onChange(e.value);
                  onOpenChange(false);
                }}
                onMouseDown={(e) => e.preventDefault()}
              >
                {e.label}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
